import {
    getDashboard as getCasinoDashboard,
    getReports as getCasinoReports,
    getContent as getCasinoContent,
} from '../../casino/CasinoMenuItems';

import * as Constants from '../../helpers/Constants';

import {
    SidebarCategory,
    SidebarCategoryDropdownItem,
    SidebarCategoryItem,
    createSidebarCategoryDropdownItem,
    createSidebarCategoryItem,
} from './SidebarCategoryTypes';

import LoggedAdmin from 'Api/Models/LoggedAdmin';
import { PERMISSION, ROLE } from 'crm/helpers/Permissions';
import { ROLES } from 'crm/services/AdminPolicyService';
import Auth from 'crm/services/Auth';
import axiosCRM from 'crm/services/MyAxios';

export function getDashboardCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_METRICS)) {
        items.push(createSidebarCategoryItem('Lottery dashboard', 'mdi-view-dashboard', '/'));
    }

    if (Constants.IS_CASINO_ENABLED) {
        const casinoDashboard: SidebarCategoryItem = getCasinoDashboard(user);
        if (casinoDashboard) {
            items.push(casinoDashboard);
        }
    }

    return {
        name: 'Dashboard',
        showCategoryDots: true,
        items,
    };
}

export function getUsersCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_USER)) {
        items.push(createSidebarCategoryItem('Users', 'mdi-ticket-account', '/users'));
    }

    return {
        showCategoryDots: false,
        items,
    };
}

export function getTicketsCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_TICKET)) {
        items.push(createSidebarCategoryItem('Tickets', 'mdi-ticket', '/tickets'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_TICKET)) {
        items.push(createSidebarCategoryItem('Multidraw Tickets', 'mdi-ticket', '/tickets_multi_draw'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_SCRATCHCARDS_TICKET)) {
        items.push(createSidebarCategoryItem('Scratchcards Tickets', 'mdi-ticket', '/tickets_scratchcards'));
    }

    return {
        name: 'Tickets',
        showCategoryDots: true,
        items,
    };
}

export function getLootboxCategory(user: LoggedAdmin): SidebarCategory {
    const lootboxItems = [
        {
            permission: PERMISSION.VIEW_LOOTBOX,
            label: 'Lootboxes',
            icon: 'mdi-package-variant-closed',
            path: '/lootboxes',
        },
        {
            permission: PERMISSION.VIEW_LOOTBOX,
            label: 'Lootbox Tickets',
            icon: 'mdi-ticket',
            path: '/lootbox_tickets',
        },
        {
            permission: PERMISSION.VIEW_LOOTBOX,
            label: 'Lootbox Rewards',
            icon: 'mdi-gift',
            path: '/lootbox_rewards',
        },
        {
            permission: PERMISSION.VIEW_LOOTBOX,
            label: 'Lootbox Pools',
            icon: 'mdi-cards',
            path: '/lootbox_pools',
        },
    ];

    const items = lootboxItems
        .filter(item => Auth.userCan(user, item.permission))
        .map(item => createSidebarCategoryItem(
            item.label,
            item.icon,
            item.path
        ));

    return {
        name: 'Lootboxes',
        showCategoryDots: true,
        items,
    };
}

export function getCasinoReportsCategory(user: LoggedAdmin): SidebarCategory {
    let items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[];

    if (Constants.IS_CASINO_ENABLED) {
        items = getCasinoReports(user);
    }

    return {
        name: 'Casino Reports',
        showCategoryDots: true,
        items,
    };
}

export function getPaymentsCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_DEPOSIT)) {
        items.push(createSidebarCategoryItem('Deposits', 'mdi-cash', '/deposits'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_PAYMENT_METHOD)) {
        items.push(createSidebarCategoryItem('Payments methods', 'mdi-cash', '/payment_methods'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_ANY_PAYMENT_METHOD_CHANNEL)) {
        items.push(createSidebarCategoryItem('Payments channels', 'mdi-cash', '/payment_method_channels'));
    }

    return {
        name: 'Payments',
        showCategoryDots: true,
        items,
    };
}

export function getWithdrawalsCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_WITHDRAWAL)) {
        items.push(createSidebarCategoryItem('Withdrawals', 'mdi-cash-multiple', '/withdrawals'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_WITHDRAWAL_METHOD)) {
        items.push(createSidebarCategoryItem('Withdrawals methods', 'mdi-cash-multiple', '/withdrawal_methods'));
    }

    return {
        name: 'Withdrawals',
        showCategoryDots: true,
        items,
    };
}

export function getAffilatesCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_ANY_ADMIN) && Auth.userHasAnyRole(user, ROLES.MAIN_ADMINS)) {
        items.push(createSidebarCategoryItem('Master affiliate admins', 'mdi-account-key', '/master_affiliate_agents'));
    }

    if (
        Auth.userCan(user, PERMISSION.VIEW_ANY_ADMIN) &&
        Auth.userHasAnyRole(user, [ROLE.MASTER_AFFILIATE_AGENT, ...ROLES.MAIN_ADMINS])
    ) {
        items.push(createSidebarCategoryItem('Affiliate admins', 'mdi-account-key', '/affiliate_owners'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_AFFILIATE) && !Auth.userHasRole(user, ROLE.MASTER_AFFILIATE_AGENT)) {
        items.push(createSidebarCategoryItem('Affiliate links', 'mdi-link-variant', '/affiliates'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_SUB_AFFILIATE) && !Auth.userHasRole(user, ROLE.MASTER_AFFILIATE_AGENT)) {
        items.push(createSidebarCategoryItem('Sub affiliate links', 'mdi-link-variant', '/sub-affiliates'));
    }

    return {
        name: 'Affilates',
        showCategoryDots: true,
        items,
    };
}

export function getAgentsCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_ADMIN) && Auth.userHasAnyRole(user, ROLES.MAIN_ADMINS)) {
        items.push(createSidebarCategoryItem('Master agents', 'mdi-account-star', '/master_agents'));
    }

    if (
        Auth.userCan(user, PERMISSION.VIEW_ADMIN) &&
        Auth.userHasAnyRole(user, [ROLE.MASTER_AGENT, ...ROLES.MAIN_ADMINS])
    ) {
        items.push(createSidebarCategoryItem('Super agents', 'mdi-account-star', '/super_agents'));
    }

    const agentsDropdownItems: SidebarCategoryItem[] = getAgentsDropdownItems(user);
    if (agentsDropdownItems.length > 0) {
        items.push(createSidebarCategoryDropdownItem('Agents', 'mdi-account-star', agentsDropdownItems));
    }

    return {
        name: 'Agents',
        showCategoryDots: true,
        items,
    };
}

export function getAgentsDropdownItems(user: LoggedAdmin): SidebarCategoryItem[] {
    const items: SidebarCategoryItem[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_RESELLER)) {
        items.push(createSidebarCategoryItem('Resellers list', 'mdi-view-module', '/resellers'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_RESELLER_APPLICANT)) {
        items.push(createSidebarCategoryItem('Resellers applications', 'mdi-view-module', '/resellers/applications'));
    }

    return items;
}

export function getVouchersCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_VOUCHER)) {
        items.push(createSidebarCategoryItem('Vouchers', 'mdi-percent', '/vouchers'));
    }

    return {
        showCategoryDots: false,
        items,
    };
}

export function getPromocodesCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_PROMO_CODE)) {
        items.push(createSidebarCategoryItem('Promocodes', 'mdi-percent', '/promo_codes'));
    }

    return {
        showCategoryDots: false,
        items,
    };
}

export function getContentCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    const articlesDropdownItems: SidebarCategoryItem[] = getArticlesDropdownItems(user);
    if (articlesDropdownItems.length > 0) {
        items.push(createSidebarCategoryDropdownItem('Articles', 'mdi-account', articlesDropdownItems));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_FAQ)) {
        items.push(createSidebarCategoryItem('FAQ', 'mdi-comment-question-outline', '/faq'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_FAQ)) {
        items.push(createSidebarCategoryItem('Faq Agents list', 'mdi-comment-question-outline', '/faq/agents'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_FAQ_CATEGORY)) {
        items.push(createSidebarCategoryItem('FAQ categories', 'mdi-comment-processing-outline', '/faq_category'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_NEWS)) {
        items.push(createSidebarCategoryItem('News', 'mdi-newspaper', '/news'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_SEO_TAG)) {
        items.push(createSidebarCategoryItem('SEO tags', 'mdi-code-tags', '/seo_tags'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_FILE_MANAGER)) {
        items.push(createSidebarCategoryItem('File manager', 'mdi-folder-move', '/file_manager'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_CUSTOM_BOX)) {
        items.push(createSidebarCategoryItem('Custom boxes', 'mdi-bulletin-board', '/custom_box'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_SLIDER)) {
        items.push(createSidebarCategoryItem('Sliders', 'mdi-account', '/sliders'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_MANUAL_NOTIFICATION)) {
        items.push(createSidebarCategoryItem('Notifications', 'mdi-bell', '/notifications'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_POSTBACK)) {
        items.push(createSidebarCategoryItem('Postbacks', 'mdi-backburger', '/postbacks'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_MENU)) {
        items.push(createSidebarCategoryItem('Menu lists', 'mdi-format-list-bulleted', '/menus'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_PAGE_SETTINGS)) {
        items.push(createSidebarCategoryItem('Page settings', 'mdi-settings', '/page-settings'));
    }

    const missingTranslationDropdownItems: SidebarCategoryItem[] = getMissingTranslationsDropdownItems(user);
    if (articlesDropdownItems.length > 0) {
        items.push(
            createSidebarCategoryDropdownItem('Missing translations', 'mdi-account', missingTranslationDropdownItems)
        );
    }

    if (Auth.userCan(user, PERMISSION.VIEW_CUSTOM_REDIRECT)) {
        items.push(createSidebarCategoryItem('Custom redirects', 'mdi-arrow-expand', '/custom-redirects'));
    }

    return {
        name: 'Content',
        showCategoryDots: true,
        items,
    };
}

export function getArticlesDropdownItems(user: LoggedAdmin): SidebarCategoryItem[] {
    const items: SidebarCategoryItem[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_ARTICLE)) {
        items.push({
            name: 'Articles',
            icon: 'mdi-view-module',
            path: '/articles',
        });
    }

    if (Auth.userCan(user, PERMISSION.VIEW_ARTICLE)) {
        items.push({
            name: 'System pages articles',
            icon: 'mdi-view-module',
            path: '/articles/partials',
        });
    }

    return items;
}

export function getMissingTranslationsDropdownItems(user: LoggedAdmin): SidebarCategoryItem[] {
    const items: SidebarCategoryItem[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_ARTICLE)) {
        items.push({
            name: 'Articles',
            icon: 'mdi-view-module',
            path: '/missing-translations/articles',
        });
        items.push({
            name: 'System pages articles',
            icon: 'mdi-view-module',
            path: '/missing-translations/partials',
        });
    }

    return items;
}

export function getCasinoContentCategory(user: LoggedAdmin): SidebarCategory | null {
    let items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[];

    if (Constants.IS_CASINO_ENABLED) {
        items = getCasinoContent(user);
    }

    return {
        name: 'Casino content',
        showCategoryDots: true,
        items,
    };
}

export function getLotteriesCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_LOTTERY)) {
        items.push(createSidebarCategoryItem('Lotteries', 'mdi-dice-5', '/lotteries'));
    }

    return {
        showCategoryDots: false,
        items,
    };
}

export function getBlockedCountriesCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (Auth.userCan(user, PERMISSION.VIEW_ANY_BLOCKED_COUNTRY)) {
        items.push(createSidebarCategoryItem('Blocked countries', 'mdi-block-helper', '/blocked-countries'));
    }

    return {
        showCategoryDots: false,
        items,
    };
}

export function getAdministratorsCategory(user: LoggedAdmin): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    if (
        Auth.userCan(user, PERMISSION.VIEW_ADMIN) &&
        !Auth.userHasRole(user, ROLE.MASTER_AGENT) &&
        !Auth.userHasRole(user, ROLE.MASTER_AFFILIATE_AGENT)
    ) {
        items.push(createSidebarCategoryItem('Administrators', 'mdi-security', '/admins'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_ADMIN) && Auth.userHasAnyRole(user, ROLES.SUPER_ADMINS)) {
        items.push(createSidebarCategoryItem('Super administrators', 'mdi-security', '/super-admins'));
    }

    if (Auth.userCan(user, PERMISSION.VIEW_DATABASE_LOGS) && Auth.userHasAnyRole(user, ROLES.SUPER_ADMINS)) {
        items.push(createSidebarCategoryItem('Admin activity logs', 'mdi-message-bulleted', '/admin-activity-logs'));
    }

    return {
        name: 'Administrators',
        showCategoryDots: true,
        items,
    };
}

export function getProfileCategory(): SidebarCategory {
    const items: (SidebarCategoryItem | SidebarCategoryDropdownItem)[] = [];

    items.push(createSidebarCategoryItem('Profile', 'mdi-account-edit', '/profile'));

    return {
        showCategoryDots: false,
        items,
    };
}

export function getLogoutItem(): SidebarCategory {
    const clickCallback = () => {
        Auth.signOut(axiosCRM);
    };

    return {
        showCategoryDots: false,
        items: [createSidebarCategoryItem('Logout', 'mdi-logout', '/', clickCallback)],
    };
}
